import type { ComponentType, CSSProperties } from 'react';
import { Check, Download, MessageCircle } from 'lucide-react';
import { Button } from '@rocco/components/button';

import { detailCta } from './detail-cta';

/** Glyph shown in place of a thumbnail. */
type FileGlyph = ComponentType<{ className?: string; strokeWidth?: number }>;

export interface DownloadFileRowProps {
    /** File name, shown as the row's heading. */
    title: string;
    /** Sub-line under the title, and the "File uploaded by" column's value. */
    uploader: string;
    /** The "File" column — the file's category, or a fixed label. */
    fileLabel: string;
    /** The "Filetype" column, e.g. `PDF` or `Link`. */
    format: string;
    /** The "Size" column, already formatted; pass an em dash when unknown. */
    size: string;
    /** Glyph used on mobile, and on desktop when there is no thumbnail. */
    Icon: FileGlyph;
    /** Desktop-only thumbnail; the glyph stands in when absent. */
    thumbnailSrc?: string;
    /** Shows a tick beside the title once the file has been actioned. */
    hasDownloaded?: boolean;
    onDownload: () => void;
    /** Adds the enquire affordance beside the download button. */
    onEnquire?: () => void;
    /** Entrance animation, applied to the row's outer element. */
    style?: CSSProperties;
}

/** A stacked label/value cell in the desktop row. */
const DownloadField = ({
    label,
    value,
    className,
}: {
    label: string;
    value: string;
    className?: string;
}) => (
    <div className={`min-w-0 shrink-0 ${className ?? ''}`}>
        <p className="m-0 font-sans text-[0.875rem] leading-tight text-[#767676]">
            {label}
        </p>
        <p
            className="m-0 mt-1.5 truncate font-sans text-[1rem] leading-tight font-medium text-[#141414]"
            title={value}
        >
            {value}
        </p>
    </div>
);

/**
 * The revamped detail pages' download row (Figma 1846:9068): thumbnail or file
 * glyph, the title beside its uploader, then the documented-by / file / format
 * / size columns and the download affordance. Mobile collapses to an editorial
 * hairline row carrying the same title and format/size.
 *
 * Purely presentational — gating, tracking and link handling belong to the
 * page that owns the file, which passes the outcome in as `onDownload`. The
 * download control is a labelled detail CTA (not an icon-only button) so it
 * stays distinct from the optional enquire action on the same row.
 */
export const DownloadFileRow = ({
    title,
    uploader,
    fileLabel,
    format,
    size,
    Icon,
    thumbnailSrc,
    hasDownloaded = false,
    onDownload,
    onEnquire,
    style,
}: DownloadFileRowProps) => {
    const actionButtons = (
        <div className="flex shrink-0 items-center gap-2">
            {onEnquire && (
                <Button
                    color="transparent"
                    size="icon"
                    className="h-8 w-8 cursor-pointer rounded-lg text-black/50 transition-all duration-200 ease-out hover:scale-110 hover:bg-black/10 hover:text-black active:scale-95"
                    onClick={onEnquire}
                    aria-label="Enquire about this file"
                >
                    <MessageCircle className="h-4 w-4" />
                </Button>
            )}
            <button
                type="button"
                onClick={onDownload}
                className={detailCta(
                    'filled-light',
                    'h-9 shrink-0 gap-1.5 px-4 text-sm',
                )}
            >
                <Download className="size-3.5" strokeWidth={2} aria-hidden />
                Download
            </button>
        </div>
    );

    return (
        <div
            className="group md:overflow-hidden md:rounded-lg md:bg-[#f6f6f6]"
            style={style}
        >
            {/* Desktop: thumbnail + title + columns + actions. */}
            <div className="hidden items-center gap-[1.5rem] px-5 py-[0.625rem] md:flex">
                <div className="flex size-[5rem] shrink-0 items-center justify-center overflow-hidden rounded">
                    {thumbnailSrc ? (
                        <img
                            src={thumbnailSrc}
                            alt=""
                            loading="lazy"
                            className="size-full object-contain"
                        />
                    ) : (
                        <Icon
                            className="h-6 w-6 text-black/30"
                            strokeWidth={1.5}
                        />
                    )}
                </div>
                <div className="flex w-[24.875rem] min-w-0 shrink-0 flex-col gap-[2px]">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <p
                            className="m-0 min-w-0 truncate font-sans text-[1rem] leading-tight font-medium text-[#141414]"
                            title={title}
                        >
                            {title}
                        </p>
                        {hasDownloaded && (
                            <Check
                                className="text-green-500 h-3.5 w-3.5 shrink-0 animate-tick-pop-in"
                                strokeWidth={3}
                            />
                        )}
                    </div>
                    <p
                        className="m-0 truncate font-sans text-[0.875rem] leading-tight text-[#6e6e6e]"
                        title={uploader}
                    >
                        {uploader}
                    </p>
                </div>
                <DownloadField
                    label="File uploaded by"
                    value={uploader}
                    className="w-[16.25rem]"
                />
                <DownloadField
                    label="File"
                    value={fileLabel}
                    className="w-[15.9375rem]"
                />
                <DownloadField
                    label="Filetype"
                    value={format}
                    className="w-[12.9375rem]"
                />
                <DownloadField
                    label="Size"
                    value={size}
                    className="w-[7.625rem]"
                />
                <div className="flex-1" />
                {actionButtons}
            </div>

            {/* Mobile: editorial hairline row — a neutral file glyph, the title
                with its format/size, and the download affordance. No coloured
                file-type badge or filled card. */}
            <div className="flex items-center gap-3.5 border-b border-black/10 py-3.5 group-last:border-b-0 md:hidden">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#f4f4f5]">
                    <Icon className="size-4 text-[#767676]" strokeWidth={1.5} />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <p
                            className="m-0 min-w-0 truncate font-sans text-sm leading-tight font-medium text-[#181818]"
                            title={title}
                        >
                            {title}
                        </p>
                        {hasDownloaded && (
                            <Check
                                className="text-green-500 h-3.5 w-3.5 shrink-0 animate-tick-pop-in"
                                strokeWidth={3}
                            />
                        )}
                    </div>
                    <p className="m-0 font-sans text-xs leading-tight text-[#8a8a91]">
                        {format}
                        {size !== '—' ? ` · ${size}` : ''}
                    </p>
                </div>
                {actionButtons}
            </div>
        </div>
    );
};
